export interface BoardLayer {
  id: string;
  role: 'background' | 'terrain' | 'decoration' | 'foreground';
  /** Board coordinates: x, y, width, height. */
  rect: [number, number, number, number];
  source?: string;
  region?: string;
  fill?: string;
  opacity?: number;
  fit?: 'contain' | 'cover' | 'fill';
}
/** Public display geometry for one existing edge; disconnected parts allow map wrapping. */
export interface BoardEdgePath { edge: string; parts: [number, number][][] }
export interface EdgePieceSlot { x: number; y: number; angle: number; width?: number; height?: number }
export interface EdgePieceStyle { source: string; width: number; height: number; tint: 'owner' }
export interface EdgePiecePlacement { edge: string; slots: EdgePieceSlot[] }
export const BOARD_EFFECTS = ['soft-outline', 'placement-hint', 'road-arrival', 'wood-impact', 'upgrade', 'travel'] as const;
export type BoardEffect = typeof BOARD_EFFECTS[number];
export interface ObjectMatch { kind?: string; catalog?: string[]; tags?: string[]; fields?: Record<string, string | number | boolean> }
export interface BoardPreset { match: ObjectMatch; arrival?: BoardEffect; change?: BoardEffect; move?: BoardEffect }
export const PIECE_SHAPES = ['settlement', 'city', 'road', 'ship', 'robber', 'pirate', 'token', 'disc', 'harbor', 'cube'] as const;
export type PieceShape = typeof PIECE_SHAPES[number];
/** One camera framing for the 3D renderer (three-board). Board units; pitch is measured below the horizon. */
export interface CameraProfile {
  id: string; label: string;
  /** 10..90 degrees below the horizon; 90 looks straight down. */
  pitchDeg: number;
  /** Camera distance from the focus point in board units, 50..20000. */
  distance: number;
  /** 0..359 degrees, or keep the viewer's current yaw. */
  yaw: number | 'keep';
  projection?: 'perspective' | 'orthographic';
  fovDeg?: number;
  transitionMs?: number;
}
export interface TerrainStyle { match: ObjectMatch; color: string; height: number; top?: string; emissive?: string }
/** A baked standee (pieces[].standee, capability three-helpers), listed in manifest.standees by build-distribution. */
export interface StandeeStyle {
  id?: string;                         // a standees[] id (^[a-z][a-z0-9-]{0,55}$); exactly one of id and idFrom
  idFrom?: 'catalog' | 'field';        // derive the id from the object's catalog key, or from a field
  field?: string;                      // with idFrom 'field' only: the field name (ID pattern, at most 128)
  prefix?: string;                     // with idFrom only: /^[a-z0-9-]{1,24}$/, prepended to the normalised value
  height?: number;                     // 0.05..8 median-edge units; default: the standee entry's height
  mounting?: 'upright' | 'ground';     // default: the entry's
  facing?: 'fixed' | 'camera';         // default: the entry's (always fixed when the effective mounting is ground)
  edge?: 'art' | 'owner' | string;     // '#rrggbb'; default: the entry's
}
/** A thin slab for card and tile objects (pieces[].slab, capability three-helpers). */
export interface SlabStyle {
  size?: number;        // width in median-edge units 0.05..8; default item.rect.w / metric for piece items, else 0.8
  aspect?: number;      // depth/width 0.2..5; default face.size.h / face.size.w, else 1
  thickness?: number;   // fraction of width 0.005..0.5; default 0.03
  radius?: number;      // corner radius as a fraction of width 0..0.5; default face.size.radius / face.size.w, else 0.04
  color?: 'owner' | string;  // side and bottom colour; default '#f2ead6'
}
export const QUAD_ALPHA = ['cutout', 'blend'] as const;
/** A flat picture from hosted art, with no bake (pieces[].quad, capability three-helpers). */
export interface QuadStyle {
  asset?: string;                     // 'asset:<id>'; exactly one of asset and assetFrom
  assetFrom?: 'catalog' | 'field';    // derive the asset id from the catalog key, or from a field
  field?: string;                     // with assetFrom 'field' only (ID pattern, at most 128)
  prefix?: string;                    // with assetFrom only: /^[A-Za-z0-9][A-Za-z0-9:_./-]{0,63}$/
  width?: number;                     // 0.05..8 median-edge units; at most one of width and height
  height?: number;                    // 0.05..8; default 1 when neither is set
  mounting?: 'upright' | 'ground';    // default 'upright'
  facing?: 'fixed' | 'camera';        // default 'fixed'; camera needs upright
  opacity?: number;                   // 0.05..1; default 1
  alpha?: 'cutout' | 'blend';         // default 'cutout'
  alphaThreshold?: number;            // integer 1..254; default 128; cutout only
  back?: 'mirror' | 'none' | string;  // '#rrggbb'; default 'mirror'; upright only
}
export const PRIMITIVE_TYPES = ['meeple', 'pawn', 'cube', 'coin', 'disc', 'cylinder', 'hex-prism', 'gem', 'bead', 'stick'] as const;
export type PrimitiveType = typeof PRIMITIVE_TYPES[number];
/** Default footprint (larger horizontal side) per primitive, in median-edge units. */
export const PRIMITIVE_SIZE: Record<PrimitiveType, number> = { meeple: .34, pawn: .3, cube: .22, coin: .3, disc: .3, cylinder: .22, 'hex-prism': .4, gem: .2, bead: .16, stick: .5 };
/** Primitive types with a flat top that can carry a decal. */
export const DECAL_PRIMITIVES: readonly PrimitiveType[] = ['coin', 'disc', 'cylinder', 'hex-prism', 'cube'];
/** A configurable procedural piece (pieces[].primitive, capability three-helpers). Body colour comes from the style's color. */
export interface PrimitiveStyle {
  type: PrimitiveType;
  size?: number;        // footprint (larger horizontal side) in median-edge units, 0.05..4; default PRIMITIVE_SIZE[type]
  aspect?: number;      // height multiplier 0.25..4; default 1
  decal?: string;       // 'asset:<id>' | 'icon:<key>' (key matches ID)
  decalScale?: number;  // 0.2..1; default 0.8
  decalInk?: string;    // '#rrggbb' for icon decals; default '#2b2723'
}
/** A die showing the face value of an observed object field (pieces[].die, capability three-helpers). Rendering never judges legality. */
export interface DieStyle {
  sides: 4 | 6 | 8 | 10 | 12 | 20;
  valueField?: string;  // ID; default 'value'
  size?: number;        // 0.05..4; default 0.3
  ink?: string;         // '#rrggbb'; default '#2b2723'
  pips?: boolean;       // d6 only
  roll?: boolean;       // cosmetic tumble on value change; default false
}
export const DIE_SIDES = [4, 6, 8, 10, 12, 20] as const;
export interface PieceStyle { match: ObjectMatch; model?: string; standee?: StandeeStyle; slab?: SlabStyle; primitive?: PrimitiveStyle; die?: DieStyle; quad?: QuadStyle; shape?: PieceShape; color?: 'owner' | string; scale?: number; yOffset?: number; rotateDeg?: number;
  /** Action kinds for which this style is previewed as a translucent ghost on lit, still empty nodes and edges. */
  ghost?: string[];
  /** Draws nothing for matching objects (they stay in the accessible index and remain pickable by state only). */
  hidden?: boolean }
export const THREE_LAYER_MODES = ['table', 'drape', 'skip'] as const;
export type ThreeLayerMode = typeof THREE_LAYER_MODES[number];
export interface ThreeRuntime {
  cameraProfiles: CameraProfile[]; defaultProfile: string;
  terrain?: TerrainStyle[]; pieces?: PieceStyle[];
  water?: { match: ObjectMatch; color: string; animate?: boolean };
  numberTokens?: { field: string; redField?: string };
  ambient?: { sky?: string; ground?: string; table?: string };
  layers?: ThreeLayerMode; // three-helpers: how board layers draw in 3D; default 'table'
}
/**
 * `background`: the canvas colour around the board extent (default the Catan sea, #22596e).
 * `ownAspect`: the Pixi viewport takes the visual's width:height as its aspect ratio (default: the layout's own box).
 * `fillNarrow`: in the compact table shell's phone layout the board fills its area (default: a 780:720 box in a scrolling panel).
 */
export interface BoardRuntime { renderer: 'pixi' | 'three'; fallback: 'svg'; padding?: number; background?: string; ownAspect?: boolean; fillNarrow?: boolean; presets?: BoardPreset[]; three?: ThreeRuntime }
export interface BoardVisual { id: string; board: string; width: number; height: number; layers: BoardLayer[]; paths?: BoardEdgePath[]; runtime?: BoardRuntime; edgePieces?: EdgePieceStyle; placements?: EdgePiecePlacement[] }
export interface BoardVisuals { formatVersion: 'board-visuals-1'; boards: BoardVisual[] }

/** Field lists of the three-helpers styles: the single source of truth for validate.ts and the docs test (docs/three-helpers-reference.md). */
export const PIECE_STYLE_FIELDS = { required: ['match'], optional: ['model', 'standee', 'slab', 'primitive', 'die', 'quad', 'shape', 'color', 'scale', 'yOffset', 'rotateDeg', 'ghost', 'hidden'] } as const;
export const STANDEE_STYLE_FIELDS = { required: [], optional: ['id', 'idFrom', 'field', 'prefix', 'height', 'mounting', 'facing', 'edge'] } as const;
export const SLAB_STYLE_FIELDS = { required: [], optional: ['size', 'aspect', 'thickness', 'radius', 'color'] } as const;
export const PRIMITIVE_STYLE_FIELDS = { required: ['type'], optional: ['size', 'aspect', 'decal', 'decalScale', 'decalInk'] } as const;
export const DIE_STYLE_FIELDS = { required: ['sides'], optional: ['valueField', 'size', 'ink', 'pips', 'roll'] } as const;
export const QUAD_STYLE_FIELDS = { required: [], optional: ['asset', 'assetFrom', 'field', 'prefix', 'width', 'height', 'mounting', 'facing', 'opacity', 'alpha', 'alphaThreshold', 'back'] } as const;
